import { Link, useParams, useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, CardHeader, ContentLoader } from '@/components/common';
import { getErrorMessage } from '@/services';
import { formatDateTime } from '@/utils';
import {
  ENQUIRY_SOURCE_CUSTOMER,
  ENQUIRY_SOURCE_MICROSITE,
  useQuoteEnquiryDetail,
} from './quoteEnquiries';

/**
 * One quote request, whichever table it came from.
 *
 * Before this screen existed, a microsite enquiry opened at `/leads/<id>` — so
 * reading a quote request meant a detour through Contacts. Both sources now
 * land here. The originating record is still reachable, as a LINK rather than
 * as the destination; that distinction is the entire point.
 */
export function QuoteRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || ENQUIRY_SOURCE_CUSTOMER;

  const { data, isLoading, isError, error } = useQuoteEnquiryDetail(source, id);

  if (isLoading) return <ContentLoader />;

  if (isError || !data) {
    return (
      <div>
        <PageHeader
          title="Quote Request"
          breadcrumbs={[{ label: 'Quotes', href: '/quotes' }, { label: 'Quote Request' }]}
        />
        <Card>
          <CardHeader
            title="Could not load this quote request"
            subtitle={isError ? getErrorMessage(error) : 'It may have been removed.'}
          />
        </Card>
      </div>
    );
  }

  const fromMicrosite = data.source === ENQUIRY_SOURCE_MICROSITE;

  // Work this enquiry up into a quote instead of starting Create Quote again.
  //
  // The Quote Requests feed is read-only on the server, and this screen was
  // read-only with it -- so an enquiry that arrived from the website could be
  // looked at and nothing else. The wizard prefills the contact from the
  // enquiry (`?enquiry=`) and pre-selects the type they asked about; a
  // customer-portal request also carries its contact, which is better
  // identification than three loose strings, so that is passed when present.
  const quoteParams = new URLSearchParams();
  if (data.insurance_type_code) quoteParams.set('type', data.insurance_type_code.toLowerCase());
  if (data.customer_profile_id) quoteParams.set('customer', data.customer_profile_id);
  quoteParams.set('enquiry', `${data.source}:${data.id}`);

  return (
    <div>
      <PageHeader
        title="Quote Request"
        subtitle={data.reference || undefined}
        breadcrumbs={[
          { label: 'Quotes', href: '/quotes' },
          { label: data.reference || 'Quote Request' },
        ]}
        actions={
          <Link to={`/quotes/new?${quoteParams.toString()}`}>
            <Button>Create Quote</Button>
          </Link>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Contact" />
          <dl className="space-y-3 text-sm">
            <Field label="Name" value={data.name} />
            <Field label="Email" value={data.email} />
            <Field label="Phone" value={data.phone} />
          </dl>
        </Card>

        <Card>
          <CardHeader title="Request" />
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500 dark:text-gray-400">Status</dt>
              <dd>
                <Badge size="sm">{data.status_display || data.status}</Badge>
              </dd>
            </div>
            <Field label="Insurance type" value={data.insurance_type} />
            <Field label="Source" value={data.source_display} />
            <Field
              label="Received"
              value={data.created_at ? formatDateTime(data.created_at) : ''}
            />
            <Field label="Assigned to" value={data.assigned_to} />
            <Field label="Insurer preference" value={data.insurer_preference} />
          </dl>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="What they asked for" />
          <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
            {data.message || 'No details were provided.'}
          </p>

          {data.source_record_path && (
            <p className="mt-6 text-sm">
              <Link
                to={data.source_record_path}
                className="text-primary-600 hover:underline dark:text-primary-400"
              >
                {fromMicrosite ? 'View source lead' : 'View quote request record'}
              </Link>
              <span className="ml-2 text-gray-500 dark:text-gray-400">
                {fromMicrosite
                  ? '— this enquiry also appears under Contacts as a lead.'
                  : '— the underlying request record.'}
              </span>
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-right font-medium text-gray-900 dark:text-gray-100">
        {value?.trim() || '—'}
      </dd>
    </div>
  );
}
